import { useCallback } from "react";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import {
  useFloatingWindowsActions,
  useIsFloatingWindowPinned,
} from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { Icon } from "src/base-components/Icon";
import { mdService } from "src/base-components/TextEditor/MarkdownService";
import { CaseActionFormField } from "src/cases/caseView/components/CaseActionFormWindow";
import { useCaseActionFormSchema } from "src/cases/caseView/hooks";
import { CaseActionFormValues } from "src/cases/caseView/schema";
import { transformFormValuesToPayload } from "src/cases/caseView/utils";
import { CaseTemplateActionOutput } from "src/clients/case-management";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/datasets/DatasetTable/FloatingWindow";

type BulkCaseActionData = {
  actionInTemplate: CaseTemplateActionOutput;
  selectedCasesCount: number;
  disabled?: boolean;
  onSubmit: (
    action: CaseTemplateActionOutput,
    formData?: CaseActionFormValues,
  ) => Promise<void>;
};

export const BulkCaseActionWindow: React.FC<FloatingWindowProps> = ({
  id,
  computePosition,
  data,
}) => {
  const { close, setPin } = useFloatingWindowsActions();
  const isPinned = useIsFloatingWindowPinned(id);
  const { position, windowRef } = useFloatingWindowPosition(computePosition);

  const { actionInTemplate, selectedCasesCount, disabled, onSubmit } =
    data as BulkCaseActionData;

  const selectedActionSchema = useCaseActionFormSchema(actionInTemplate);

  const defaultValues: Record<string, unknown> = {
    action_key: actionInTemplate.key,
  };

  actionInTemplate.form?.fields?.forEach((field) => {
    defaultValues[field.key] = field.type === "multi_select" ? [] : null;
  });

  const form = useForm<CaseActionFormValues>({
    mode: "onBlur",
    defaultValues,
    resolver: yupResolver(selectedActionSchema),
  });

  const {
    formState: { isValid },
  } = form;

  const handleSubmit = useCallback(async () => {
    if (!isValid) return;
    const values = form.getValues();
    const transformedValues = transformFormValuesToPayload(
      selectedActionSchema,
      values,
    );
    await onSubmit(actionInTemplate, transformedValues);
    close(id);
  }, [
    close,
    id,
    isValid,
    form,
    actionInTemplate,
    onSubmit,
    selectedActionSchema,
  ]);

  const handleClose = useCallback(() => close(id), [close, id]);

  const togglePin = useCallback(
    () => setPin(id, !isPinned),
    [setPin, id, isPinned],
  );

  return (
    <FloatingWindowInternal
      ref={windowRef}
      clipOverflow={false}
      dataLoc="bulk-case-action-form-window"
      headerAlignment="start"
      height="h-96"
      id={id}
      isPinned={isPinned}
      style={{
        inset: "40px auto auto 4px",
        transform: position,
      }}
      title={
        <div className="flex min-w-0 flex-col gap-y-1">
          <p className="font-inter-semibold-13px text-gray-800">
            {actionInTemplate.display_name}
          </p>
          {actionInTemplate.description && (
            <div
              //skipcq: JS-0440
              dangerouslySetInnerHTML={{
                __html: mdService.toHtml(actionInTemplate.description),
              }}
              className="tktl-markdown font-inter-normal-12px text-gray-500"
            />
          )}
        </div>
      }
      draggable
      maximizable
      resizeable
      onClose={handleClose}
      onPin={togglePin}
    >
      <div className="relative -mx-2 flex h-full flex-col justify-between">
        <div className="decideScrollbar p-4">
          <FormProvider {...form}>
            <form className="flex flex-col gap-y-3">
              {actionInTemplate.form?.fields.map((field) => (
                <CaseActionFormField
                  key={field.key}
                  disabled={disabled}
                  field={field}
                />
              ))}
            </form>
          </FormProvider>
        </div>

        <div className="flex items-center justify-between gap-x-3 border-gray-100 border-t p-4">
          <div className="flex w-full gap-x-1.5 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 font-inter-normal-12px text-gray-600">
            <Icon color="text-gray-500" icon={faInfoCircle} size="xs" />
            <span>
              This action will be applied to all {selectedCasesCount} selected
              cases.
            </span>
          </div>
          <Button
            dataLoc="bulk-case-action-submit"
            disabled={!isValid || disabled}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </div>
      </div>
    </FloatingWindowInternal>
  );
};

type BulkCaseActionModalProps = {
  isOpen: boolean;
  action: CaseTemplateActionOutput | null;
  selectedCasesCount: number;
  onClose: () => void;
  onSubmit: (action: CaseTemplateActionOutput) => Promise<void>;
};

export const BulkCaseActionModal: React.FC<BulkCaseActionModalProps> = ({
  isOpen,
  action,
  selectedCasesCount,
  onClose,
  onSubmit,
}) => {
  const handleConfirm = useCallback(async () => {
    if (!action) return;
    await onSubmit(action);
    onClose();
  }, [action, onSubmit, onClose]);

  if (!action) return null;

  return (
    <ConfirmationModal
      confirmationButtonText="Submit"
      open={isOpen}
      title={`Are you sure you want to submit ${action.display_name}?`}
      onClose={onClose}
      onConfirm={handleConfirm}
    >
      <div className="font-inter-normal-13px text-gray-500">
        This action will be applied to all {selectedCasesCount} selected cases
        and can't be undone.
      </div>
    </ConfirmationModal>
  );
};
